import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLoyaltyContext } from '@pegasus/plus';
import { useConfig } from '@browsing/config';
import { getQueryParam } from '@browsing/utils';

import { MyLoungeResponse } from '@shared/types/mylounge';
import { getCampaigns, getPersonalizedCampaigns } from '@client/services/api/mylounge';
import { getMylounge, fetchingCampaigns } from '@client/redux/modules/mylounge';
import { useVariant } from '@client/lib/assignments';
import { makeRequestedLoyaltyAccessParam } from '@browsing/plus';

export const useMylounge = () => {
    const dispatch = useDispatch();
    const config = useConfig();

    const { isEnabled: isLoyaltyEnabled } = useLoyaltyContext();
    const isLoyaltyEarlyAccessEligibleEnabled = useVariant(
        'loyalty_ea_eligible',
        'variant_with_ea',
    );
    const requestedLoyaltyAccess = makeRequestedLoyaltyAccessParam(
        isLoyaltyEnabled,
        isLoyaltyEarlyAccessEligibleEnabled,
    );

    const { openCampaigns, upcomingCampaigns, tabs, globalRetailEvents, loading } =
        useSelector(getMylounge);

    // fallback in case of SSR failure
    useEffect(() => {
        const fetchMylounge = async () => {
            try {
                dispatch(fetchingCampaigns.started(null));

                let mylounge: MyLoungeResponse | null = null;
                if (config.isEnabled('upcoming')) {
                    const startedDate = getQueryParam('date') || new Date(Date.now()).toISOString();

                    mylounge = await getCampaigns(startedDate, {
                        requestedLoyaltyAccess,
                    });
                } else {
                    mylounge = await getPersonalizedCampaigns({
                        requestedLoyaltyAccess,
                    });
                }

                dispatch(
                    fetchingCampaigns.done({
                        result: mylounge,
                    }),
                );
            } catch (err) {
                dispatch(
                    fetchingCampaigns.failed({
                        error: 'Fetching campaigns failed',
                    }),
                );
            }
        };

        if (!loading && (!openCampaigns?.length || !upcomingCampaigns?.length || !tabs?.length)) {
            fetchMylounge();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestedLoyaltyAccess]);

    return { openCampaigns, upcomingCampaigns, tabs, globalRetailEvents };
};
